import React, { FC, useMemo, useState } from "react";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { timeText } from "../../helpers/preview-text";
import { isOnDemandViewable } from "../../helpers/previewHelpers";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { PlayerRefreshEvent } from "../../types/common";
import { CountDownTimer, TimerState } from "../CountDownTimer/CountDownTimer";

import {
  NonPlayableVideoOverlay,
  NonPlayableVideoText,
  TextNoWrap,
  WithAnimatedEllipsis
} from "./NonPlayableStyles";

interface Props {
  refreshComponent: PlayerRefreshEvent;
}

export const PendingEvent: FC<Props> = ({ refreshComponent }) => {
  const { useStoreState } = usePlayerStore();
  const { countryCode } = useStoreState.geolocation();
  const { language } = useEmbedParametersContext();
  const { nextOndemand, startTime } = useMediaContext();

  const [timerState, setTimerState] = useState<TimerState>(TimerState.Pending);

  const timerStateText = useMemo(() => {
    switch (timerState) {
      case TimerState.Pending: {
        const translateKey = isOnDemandViewable(countryCode, nextOndemand)
          ? "ohjelma_on_tulossa_katsottavaksi"
          : "ohjelma_on_katsottavissa_vain_suorana";
        return (
          <>
            {i18n(translateKey, { language })}{" "}
            <TextNoWrap>{timeText(language, startTime)}</TextNoWrap>.
          </>
        );
      }
      case TimerState.Running:
      case TimerState.Completed:
        return i18n("timer_program_starts_soon", { language });
      case TimerState.OnOverTime:
        return (
          <WithAnimatedEllipsis>
            {i18n("timer_completed", { language })}
          </WithAnimatedEllipsis>
        );
      case TimerState.Failed:
        return i18n("timer_failed", { language });
    }
  }, [countryCode, language, nextOndemand, startTime, timerState]);

  return (
    <NonPlayableVideoOverlay>
      <NonPlayableVideoText>{timerStateText}</NonPlayableVideoText>
      <CountDownTimer
        onTimerEnd={refreshComponent}
        onTimerStateChange={setTimerState}
      />
    </NonPlayableVideoOverlay>
  );
};
